/**
 * AI compose bar — restyle the draft already in the composer.
 *
 * Sits under the editor as a single hairline card: an AI badge, the rewrite
 * actions as inline chips, undo/redo, and a "Custom" row that opens on demand
 * for a free-text instruction. Calls `POST /agent/ai/rewrite`.
 *
 * The actions are chips rather than an overflow menu. There are only four,
 * they are the whole point of the control, and hiding them behind a `⋮` costs
 * a click and a guess on every use. Showing them also makes it obvious what
 * the AI can do here without discovering a menu.
 *
 * The rewrite REPLACES the editor's contents, which is destructive, so the
 * previous version is pushed onto an undo stack first. Without that, one
 * mistaken "Shorten" silently destroys a long draft the agent spent minutes
 * on — the worst failure this control can have, and why undo/redo sit beside
 * the actions rather than being buried.
 *
 * The result is applied in one swap at the end rather than streamed into the
 * editor: watching your own draft be overwritten token by token is alarming,
 * and a mid-stream failure would strand a half-rewritten message in the box.
 *
 * The endpoint works in plain text, so HTML is flattened on the way in and
 * paragraphs restored on the way out. Rich formatting does not survive a
 * rewrite — another reason undo matters.
 *
 * Styling is the workspace's own: 3px corners, hairline slate-200 borders,
 * `#3366FF` accent — matching the surrounding conversation UI rather than
 * importing another product's look.
 */
import { useState, type JSX } from 'react'
import {
  Check,
  Loader2,
  Maximize2,
  Minimize2,
  PenLine,
  Redo2,
  Sparkles,
  Undo2,
  Wand2,
  X,
} from 'lucide-react'

import { useAiStream } from '../../../../hooks/useAiStream.js'
import { htmlToPlain, plainToHtml } from './text.js'

/**
 * One-tap actions, in the order an agent reaches for them. Values match the
 * endpoint's `action` enum.
 */
const ACTIONS = [
  { value: 'polish', label: 'Polish', icon: Wand2, hint: 'Fix grammar, clarity and flow' },
  { value: 'formalise', label: 'Formal', icon: PenLine, hint: 'Rewrite in formal business English' },
  { value: 'friendly', label: 'Friendly', icon: Sparkles, hint: 'Warmer, more conversational' },
  { value: 'shorten', label: 'Shorter', icon: Minimize2, hint: 'Tighten without losing facts' },
  { value: 'elaborate', label: 'Longer', icon: Maximize2, hint: 'Add helpful detail' },
] as const

export interface AiComposeBarProps {
  /** Current editor contents. HTML when `html` is true, otherwise plain text. */
  value: string
  /** Replace the editor contents with the rewritten draft. */
  onChange: (next: string) => void
  /** True when the editor holds HTML (RichTextEditor) rather than plain text. */
  html?: boolean
  disabled?: boolean
  /** Override the rewrite endpoint (default is /agent/ai/rewrite) */
  endpoint?: string
}

export default function AiComposeBar({
  value,
  onChange,
  html = false,
  disabled = false,
  endpoint = '/agent/ai/rewrite',
}: AiComposeBarProps): JSX.Element {
  const { running, error, run } = useAiStream()
  const [customOpen, setCustomOpen] = useState(false)
  const [instruction, setInstruction] = useState('')
  const [undoStack, setUndoStack] = useState<string[]>([])
  const [redoStack, setRedoStack] = useState<string[]>([])
  /** Which chip is mid-run, so the spinner lands on the one that was clicked. */
  const [activeAction, setActiveAction] = useState<string | null>(null)

  const plain = html ? htmlToPlain(value) : value
  const isEmpty = plain.trim().length === 0
  const canRun = !disabled && !running && !isEmpty

  async function apply(action: string, custom?: string): Promise<void> {
    if (!canRun) return
    setActiveAction(action)

    // Snapshot BEFORE the model touches anything.
    const before = value

    const body: Record<string, unknown> = { text: plain, action }
    if (custom) body.instruction = custom

    try {
      // `run` returns the settled result — reading the hook's `text` here
      // would see the pre-update closure value.
      const result = await run(endpoint, body)
      if (!result?.content) return

      setUndoStack((s) => [...s, before])
      // A fresh rewrite invalidates any redo: that branch is no longer
      // reachable from the current text.
      setRedoStack([])
      onChange(html ? plainToHtml(result.content) : result.content)
      setInstruction('')
      setCustomOpen(false)
    } finally {
      setActiveAction(null)
    }
  }

  function undo(): void {
    const prev = undoStack[undoStack.length - 1]
    if (prev === undefined) return
    setRedoStack((s) => [...s, value])
    setUndoStack((s) => s.slice(0, -1))
    onChange(prev)
  }

  function redo(): void {
    const next = redoStack[redoStack.length - 1]
    if (next === undefined) return
    setUndoStack((s) => [...s, value])
    setRedoStack((s) => s.slice(0, -1))
    onChange(next)
  }

  const iconBtn =
    'flex h-6 w-6 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400'

  return (
    <div className="mt-2 overflow-hidden rounded-[3px] border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5">
        {/* Badge — marks the row as AI without a heading taking a whole line. */}
        <span className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-[#3366FF]/10 px-1.5 py-1 text-[11px] font-semibold text-[#3366FF]">
          <Sparkles className="h-3 w-3" />
          AI
        </span>

        <span className="h-4 w-px shrink-0 bg-slate-200" aria-hidden />

        {ACTIONS.map(({ value: action, label, icon: Icon, hint }) => {
          const busy = running && activeAction === action
          return (
            <button
              key={action}
              type="button"
              onClick={() => void apply(action)}
              disabled={!canRun}
              title={isEmpty ? 'Write something first' : hint}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-[3px] border border-transparent px-2 py-1 text-[12px] font-medium text-slate-600 transition-all hover:border-[#3366FF]/30 hover:bg-[#3366FF]/[0.06] hover:text-[#3366FF] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-slate-600"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
              {label}
            </button>
          )
        })}

        {/* Custom instruction toggle */}
        <button
          type="button"
          onClick={() => setCustomOpen((v) => !v)}
          disabled={!canRun && !customOpen}
          title="Describe your own change"
          aria-expanded={customOpen}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-[3px] border px-2 py-1 text-[12px] font-medium transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${
            customOpen
              ? 'border-[#3366FF]/30 bg-[#3366FF]/[0.06] text-[#3366FF]'
              : 'border-transparent text-slate-600 hover:border-[#3366FF]/30 hover:bg-[#3366FF]/[0.06] hover:text-[#3366FF]'
          }`}
        >
          <PenLine className="h-3.5 w-3.5" />
          Custom
        </button>

        {/* Undo / redo. Always present, disabled when empty — a safety control
            that only appears after a destructive action cannot be relied on. */}
        <div className="ml-auto flex shrink-0 items-center gap-0.5 pl-1.5">
          <button
            type="button"
            onClick={undo}
            disabled={undoStack.length === 0 || running}
            className={iconBtn}
            title="Undo the last AI rewrite"
            aria-label="Undo the last AI rewrite"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={redoStack.length === 0 || running}
            className={iconBtn}
            title="Redo"
            aria-label="Redo"
          >
            <Redo2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {customOpen && (
        <div className="flex items-center gap-1.5 border-t border-slate-100 bg-slate-50/60 px-2 py-1.5">
          <PenLine className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <input
            value={instruction}
            autoFocus
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && instruction.trim()) {
                e.preventDefault()
                void apply('custom', instruction.trim())
              }
              if (e.key === 'Escape') setCustomOpen(false)
            }}
            disabled={disabled || running}
            placeholder="Describe your change — e.g. mention the delay and offer a callback"
            className="min-w-0 flex-1 bg-transparent text-[12px] text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={() => void apply('custom', instruction.trim())}
            disabled={!canRun || instruction.trim().length === 0}
            className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-[#3366FF] px-2 py-1 text-[11px] font-medium text-white transition-all hover:bg-[#2952CC] active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {running && activeAction === 'custom' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
            Apply
          </button>
          <button
            type="button"
            onClick={() => setCustomOpen(false)}
            className={iconBtn}
            title="Close"
            aria-label="Close custom instruction"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {error && (
        <p className="border-t border-red-100 bg-red-50/60 px-2.5 py-1.5 text-[11px] text-red-700">{error}</p>
      )}
    </div>
  )
}
